import React, { useState, useRef } from "react";
import {
  Container, Paper, Typography, Button, Grid, Table, TableBody, TableCell,
  TableHead, TableRow, LinearProgress, Chip, makeStyles
} from "@material-ui/core";
import { CloudUpload, CloudDownload, CheckCircle, Error } from "@material-ui/icons";
import { importContacts, exportContacts } from "../../services/api.new-features";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  paper: { padding: theme.spacing(3), marginTop: theme.spacing(2) },
  uploadArea: {
    border: "2px dashed #ccc", borderRadius: 8, padding: theme.spacing(4),
    textAlign: "center", cursor: "pointer", "&:hover": { borderColor: theme.palette.primary.main }
  },
  result: { padding: theme.spacing(2), marginTop: theme.spacing(2) },
}));

const ContactImportExport = () => {
  const classes = useStyles();
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState([]);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const parseCSV = (text) => {
    const lines = text.split("\n").filter(l => l.trim());
    const headers = lines[0].split(",").map(h => h.trim().replace(/"/g, "").toLowerCase());
    return lines.slice(1).map(line => {
      const vals = line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g) || [];
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (vals[i] || "").replace(/"/g, "").trim(); });
      return obj;
    });
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const contacts = parseCSV(ev.target.result);
      setPreview(contacts.slice(0, 10));
      setResult(null);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!fileRef.current?.files[0]) { toast.error("Selecione um arquivo CSV"); return; }
    setImporting(true);
    try {
      const text = await fileRef.current.files[0].text();
      const contacts = parseCSV(text);
      const res = await importContacts(contacts);
      setResult(res.data);
      toast.success(`Importação concluída! ${res.data.created} criados, ${res.data.updated} atualizados`);
    } catch (e) { toast.error("Erro na importação"); }
    setImporting(false);
  };

  const handleExport = async () => {
    try {
      const res = await exportContacts();
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "contacts.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Exportação concluída!");
    } catch (e) { toast.error("Erro na exportação"); }
  };

  return (
    <Container maxWidth="lg" className={classes.root}>
      <Typography variant="h5" gutterBottom>Importar / Exportar Contatos</Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper className={classes.paper}>
            <Typography variant="h6" gutterBottom><CloudUpload /> Importar CSV</Typography>
            <div className={classes.uploadArea} onClick={() => fileRef.current?.click()}>
              <input type="file" accept=".csv" ref={fileRef} onChange={handleFile} style={{ display: "none" }} />
              <Typography>Clique para selecionar arquivo CSV</Typography>
              <Typography variant="caption" color="textSecondary">Formato: name, number, email</Typography>
            </div>

            {preview.length > 0 && (
              <>
                <Typography variant="subtitle2" style={{ marginTop: 16 }}>Preview ({preview.length} linhas)</Typography>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Nome</TableCell><TableCell>Número</TableCell><TableCell>Email</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {preview.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell>{c.name}</TableCell><TableCell>{c.number}</TableCell><TableCell>{c.email}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Button variant="contained" color="primary" onClick={handleImport} disabled={importing}
                  style={{ marginTop: 16 }} fullWidth>
                  {importing ? "Importando..." : "Importar Contatos"}
                </Button>
                {importing && <LinearProgress style={{ marginTop: 8 }} />}
              </>
            )}

            {result && (
              <Paper className={classes.result} elevation={0} style={{ backgroundColor: "#f5f5f5" }}>
                <Grid container spacing={1}>
                  <Grid item><Chip icon={<CheckCircle />} label={`${result.created} criados`} color="primary" /></Grid>
                  <Grid item><Chip label={`${result.updated} atualizados`} /></Grid>
                  <Grid item><Chip icon={<Error />} label={`${result.errors} erros`} color="secondary" /></Grid>
                </Grid>
              </Paper>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper className={classes.paper}>
            <Typography variant="h6" gutterBottom><CloudDownload /> Exportar CSV</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Exportar todos os contatos em formato CSV (nome, número, email)
            </Typography>
            <Button variant="contained" color="primary" startIcon={<CloudDownload />}
              onClick={handleExport} fullWidth>
              Exportar Contatos
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ContactImportExport;